import React, { useEffect, useState } from 'react'
import { Button, Checkbox, ResponsiveTableRow, Row } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import { usePageView } from '@fs/zion-analytics'
import { useParams, useRouteMatch } from '@fs/zion-router'
import { useFeatureFlag } from '@fs/zion-flags'
import { DmcResponsiveTable } from '../../common/DmcResponsiveTable'
import { usePageTitle } from '../../app/PageTitleContext'
import useNotifications from '../../../hooks/useNotifications'
import { DmcText } from '../../common/DmcText'
import { DmcGrid } from '../../common/DmcGrid'
import { DmcRow } from '../../common/DmcRow'
import { getQueues } from '../../../services/dmqueuesService'
import { navigationQueryParams, treeCategoryMap } from '../messagesList/useMessagesListController'
import { getStreamQueueStatus } from '../../../services/chatService'

const CATEGORY_OPTIONS = [treeCategoryMap.familyTree, treeCategoryMap.cet, treeCategoryMap.secondary]

const HISTORICAL_V2 = 'HISTORICAL_PERSON_V2'
const HISTORICAL_V1 = 'HISTORICAL_PERSON'

/**
 * Shows one historical queue, never both -- two would double the apparent workload (DATA-12381 BR-40).
 * Every other queue passes through untouched.
 */
export function hideTheOtherHistoricalQueue(payload, v2IsOn) {
  const hidden = v2IsOn ? HISTORICAL_V1 : HISTORICAL_V2
  const drop = (list) => list.filter((queue) => queue?.name !== hidden)

  // The service returns { queues: [...] }, not a bare array. An earlier version of this function only
  // handled the array and silently returned the payload untouched, so the flag appeared to do nothing.
  if (Array.isArray(payload?.queues)) return { ...payload, queues: drop(payload.queues) }
  if (Array.isArray(payload)) return drop(payload)
  return payload
}

export default function QueuesList() {
  usePageView({ page_detail: `DMConsole:QueuesList:${useParams().queuesFilter}` })
  const { t } = useTranslation()
  usePageTitle(t('pageTitle.queues.list'))
  const { error } = useNotifications()
  const [queuesList, setQueuesList] = useState([])
  const queuesFilter = useParams().queuesFilter
  // DATA-12381 BR-40: exactly one historical queue is visible at a time. Purely a display choice --
  // both queues keep filling, and flipping the flag back restores the other view immediately.
  const historicalV2Flag = useFeatureFlag('tree_admin_dmcsonsole_historical')

  useEffect(() => {
    if (queuesFilter) {
      getQueues({ group: queuesFilter })
        .then((queues) => {
          setQueuesList(hideTheOtherHistoricalQueue(queues.data, historicalV2Flag.isOn))
        })
        .catch((err) => {
          error(
            t('queues.errors.failedToGetDMQueues', `Failed to get Data Management Queues: {message}`, {
              message: err?.message,
            }),
            err
          )
          setQueuesList([])
        })
    } else {
      error(t('typeIsNull', 'Type is null'))
    }
  }, [queuesFilter]) // eslint-disable-line react-hooks/exhaustive-deps

  if (!queuesList) {
    return null
  }

  return <QueuesTable queuesList={queuesList} />
}

function QueuesTable({ queuesList }) {
  const { t } = useTranslation()
  const queuesFilter = useParams().queuesFilter

  const [selected, setSelected] = useState(() => [treeCategoryMap.familyTree, treeCategoryMap.cet])

  const handleCheckboxChange = (value) => {
    setSelected((prev) => (prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]))
  }

  const streamChatStatusFlag = useFeatureFlag('data_dmconsole_stream_chat_status')

  const [chatQueueSize, setChatQueueSize] = useState(null)
  const [chatQueueUrl, setChatQueueUrl] = useState(null)
  const { error } = useNotifications()

  useEffect(() => {
    const fetchStreamQueueStatus = async () => {
      if (streamChatStatusFlag.isOn && queuesFilter === 'inappropriate') {
        try {
          const response = await getStreamQueueStatus()
          setChatQueueSize(response.data?.itemsInQueue || null)
          setChatQueueUrl(response.data?.queueUrl || null)
        } catch (err) {
          error(
            t('queues.errors.failedToGetStreamQueueStatus', `Failed to get Stream queue status: {message}`, {
              message: err?.message,
            }),
            err
          )
          setChatQueueSize(null)
        }
      }
    }
    fetchStreamQueueStatus()
  }, [queuesFilter, streamChatStatusFlag.isOn, error, t])

  return (
    <>
      <Row>
        {CATEGORY_OPTIONS.map((category) => (
          <Checkbox
            key={category}
            label={t(`treeCategories.${category}`, category)}
            name={category}
            checked={selected.includes(category)}
            onChange={() => handleCheckboxChange(category)}
          />
        ))}
      </Row>
      <DmcResponsiveTable
        totalColumnCount={1}
        atSizeColumns={{
          default: {
            labels: [''],
          },
        }}
      >
        {queuesList.queues?.map((queue) => {
          return (
            <ResponsiveTableRow
              key={queue.id}
              column1={queue.messageCount > 0 && <QueueTableDetails queue={queue} selected={selected} />}
            />
          )
        })}
      </DmcResponsiveTable>
      {streamChatStatusFlag.isOn && queuesFilter === 'inappropriate' && chatQueueSize !== null && chatQueueSize > 0 && (
        <StreamChatStatus queueSize={chatQueueSize} queueUrl={chatQueueUrl} />
      )}
    </>
  )
}

function QueueTableDetails({ queue, selected }) {
  const { t } = useTranslation()
  const match = useRouteMatch()

  const noneLabel = t('treeCategories.noTreeCategory', 'No Tree Category')
  const treeCategories = queue.treeCategoryCounts?.none ? '' : selected

  return (
    <DmcGrid columns={[3, 4, 3, 0.5, 3]}>
      <DmcText text={queue.name} />
      <DmcText text={queue.description} />
      <span>
        {!queue.treeCategoryCounts && <DmcText text={queue.messageCount} />}
        {queue.treeCategoryCounts &&
          Object.entries(queue.treeCategoryCounts)
            .reduce((acc, [key, value]) => {
              const group = key === 'sharedFamilyGroup' ? 'familyTree' : key
              const existingEntry = acc.find(([k]) => k === group)
              if (existingEntry) {
                existingEntry[1] += value
              } else {
                acc.push([group, value])
              }
              return acc
            }, [])
            .filter(([key]) => selected.includes(treeCategoryMap[key]))
            .sort(([keyA], [keyB]) => {
              const indexA = CATEGORY_OPTIONS.indexOf(treeCategoryMap[keyA])
              const indexB = CATEGORY_OPTIONS.indexOf(treeCategoryMap[keyB])
              return indexA - indexB
            })
            .map(([key, value]) => {
              const label = t(`treeCategories.${treeCategoryMap[key]}`, key)
              return <DmcText key={key} text={`${label}: ${value}`} />
            })}
        {queue.treeCategoryCounts?.none && <DmcText text={`${noneLabel}: ${queue.treeCategoryCounts.none}`} />}
      </span>
      <div>
        {queue.messageCount > 0 && (
          <DmcRow>
            <Button
              to={`${match.url}/${queue.name}?${navigationQueryParams({ queuePageIndex: 0, treeCategories })}`}
            >{`${t('queues.view', 'View')}`}</Button>
          </DmcRow>
        )}
      </div>
    </DmcGrid>
  )
}

function StreamChatStatus({ queueSize, queueUrl }) {
  const { t } = useTranslation()
  return (
    <DmcGrid columns={[4, 3, 7]}>
      <DmcText
        text={t('queues.chatQueueStatus', 'There are {queueSize} items for review in the chat moderation queue.', {
          queueSize,
        })}
      />
      <DmcRow>
        <Button to={queueUrl} target="_blank">
          {t('queues.chat.viewInStream', 'View in Stream')}
        </Button>
      </DmcRow>
    </DmcGrid>
  )
}
